import { useEffect, useState, useMemo } from 'react';
import { useFeatureSettings } from '@/hooks/useFeatureSettings';

type Season = 'winter' | 'spring' | 'summer' | 'autumn';

const getSeason = (): Season => {
  const month = new Date().getMonth(); // 0-11
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'autumn';
  return 'winter';
};

const SEASON_CONFIG: Record<Season, { particles: string[]; count: number; className: string }> = {
  winter: {
    particles: ['❄', '❅', '❆', '✧', '·'],
    count: 25,
    className: 'seasonal-winter',
  },
  spring: {
    particles: ['🌸', '🌷', '✿', '❀', '🌺'],
    count: 18,
    className: 'seasonal-spring',
  },
  summer: {
    particles: ['☀', '✦', '·', '°', '✧'],
    count: 12,
    className: 'seasonal-summer',
  },
  autumn: {
    particles: ['🍂', '🍁', '🍃', '🌿'],
    count: 20,
    className: 'seasonal-autumn',
  },
};

interface Particle {
  id: number;
  char: string;
  left: number;
  delay: number;
  duration: number;
  size: number;
  opacity: number;
  swayAmount: number;
}

const SeasonalEffects = () => {
  const { data: features } = useFeatureSettings();
  const [isVisible, setIsVisible] = useState(true);
  const season = useMemo(() => getSeason(), []);
  const config = SEASON_CONFIG[season];

  // Auto-hide after 12 seconds for subtle delight
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(false), 12000);
    return () => clearTimeout(timer);
  }, []);

  // Check sessionStorage to only show once per session
  useEffect(() => {
    const key = 'seasonal-effect-shown';
    if (sessionStorage.getItem(key)) {
      setIsVisible(false);
      return;
    }
    sessionStorage.setItem(key, '1');
  }, []);

  const particles = useMemo<Particle[]>(() => {
    return Array.from({ length: config.count }, (_, i) => ({
      id: i,
      char: config.particles[Math.floor(Math.random() * config.particles.length)],
      left: Math.random() * 100,
      delay: Math.random() * 5,
      duration: 6 + Math.random() * 8,
      size: 0.6 + Math.random() * 1,
      opacity: 0.3 + Math.random() * 0.5,
      swayAmount: 20 + Math.random() * 60,
    }));
  }, [config]);

  // Check feature toggle kill switch
  if (features?.seasonal_effects_enabled === false) return null;

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden transition-opacity duration-1000"
      aria-hidden="true"
      style={{ opacity: isVisible ? 1 : 0 }}
    >
      {particles.map((p) => (
        <span
          key={p.id}
          className="seasonal-particle"
          style={{
            left: `${p.left}%`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            fontSize: `${p.size}rem`,
            opacity: p.opacity,
            '--sway': `${p.swayAmount}px`,
          } as React.CSSProperties}
        >
          {p.char}
        </span>
      ))}
    </div>
  );
};

export default SeasonalEffects;
